import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { NumericFormat } from "react-number-format";

import { inputStyle } from "./styles";
import { addBondButtonStyle, gridHeaderTextStyle } from "./listRfqStyles";
import { updateVenues, loadStrategyParamDefs, filterVenueOptions, fieldOptions } from "./optionsHelper";
import { fetchBondCalcSettlementDate, fetchMarketDataDetail, fetchSecurityInfo } from "../../http/api";
import BondDetailsModal from "../BondDetails/BondDetailsModal";
import StrategyParamsModal from "./StrategyParamsModal";
import OrderComments from "./ActualOrderTabOrderComments";
import ListRfqBondRow, {
  listRfqColumns,
  listRfqRowMinWidth,
  listRfqColumnSx,
} from "./ListRfqBondRow";
import { useListRfqStore } from "../../Store/listRfqStore";
import { findDuplicateCusips, meaningfulRows } from "./listRfqPayload";
import {
  defaultListName,
  LIST_RFQ_NAME_REQUIRED,
  MAX_LIST_RFQ_BONDS,
  MAX_LIST_RFQ_NAME_LENGTH,
  MKTX_INQUIRY_TYPES,
  MKTX_PRICING_PROCESSES,
  MKTX_PRICING_PROCESS_DISPLAY,
  MKTX_STRATEGY_TYPES,
  mktxStrategyUsesDealers,
  PROTOCOL_TYPES,
  // TRUMID_PRICE_TYPES,  // parked with the "Set all" shortcuts below
  TRUMID_RFQ_INQUIRY_TYPES,
} from "../../Constants/AppConstants";
import { tokens } from "../../Common/designTokens";
import { normalizeMarketDataDetail } from "../../Utils/marketDataDetail";

const ALL_DEALER_OPTION = "ALL";

/**
 * Renders the calculated settlement date the way the single-bond ticket renders it.
 *
 * Mirrors formatSettlementDate in ActualOrderTab (line ~1272), which produces MM/DD/YYYY.
 * It is repeated here rather than imported because that function and the
 * parseDisplayDateUTC helper it relies on are module-private to ActualOrderTab, and
 * exporting them would mean editing the working single-bond ticket.
 *
 * Only the yyyy-MM-dd shape is handled, because that is what fetchBondCalcSettlementDate
 * returns and what buildListRfqPayload sends straight back as tag 64.
 */
const formatSettlementDate = (value) => {
  if (!value) return "";
  const match = String(value).trim().match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (!match) return String(value);
  const [, yyyy, mm, dd] = match;
  return `${mm.padStart(2, "0")}/${dd.padStart(2, "0")}/${yyyy}`;
};

// Same Yes/No -> 1/0 default normalization as the single-order ticket. Kept at module
// scope so the asynchronous row and dialog loaders use one stable conversion function.
const normalizeYesNo = (val) => {
  const upper = String(val ?? "").trim().toUpperCase();
  if (upper === "YES" || upper === "Y") return "1";
  if (upper === "NO" || upper === "N") return "0";
  return val;
};

/**
 * The List RFQ tab: one enquiry covering several bonds. The protocol selection determines
 * which venue's list-level settings and per-bond fields are shown and submitted.
 *
 * The Trumid layout follows the spec's own structure rather than being a design choice. The
 * Trumid RFQ spec (pp.45-51) splits the Quote Request <R> into two levels, and so does
 * this screen:
 *
 *   MESSAGE level, one per enquiry   -> the "Enquiry" section at the top
 *       28006 ListInquiryType, 28019 GoodForTimeMinutes, 28020 DueInTimeMinutes
 *   Inside NoRelatedSym <146>, per   -> one grid row per bond
 *   line item
 *       48, 54, 38, 423, 234, 28007, 64, 453 ...
 *
 * Account sits at the top for a different reason: the spec puts CorpBookId <1> and
 * TsyBookId <20003> inside the group, but our backend derives both from the single account
 * it resolved for the list and handleTrumidListProtocol rejects a list spanning accounts.
 * So one account per list is our constraint, and the UI shows what the backend accepts.
 *
 * 28013 AlltoAll is not offered: createListRfqDto hardcodes 'N' (fully disclosed), as the
 * single-bond path does, so there is nothing to choose.
 *
 * <p><b>Why TruMidRFQFields is not reused here.</b> That component renders inquiry type,
 * deadline, price type, stipulation and partial fills together, which is right for a single
 * bond where all five describe the same instrument. On a list those five straddle the two
 * levels above, so reusing it would put per-line-item fields in the enquiry header - which
 * is exactly the mistake the first version of this screen made. Its limits and labels are
 * deliberately mirrored below.
 */
const ListRfqTab = ({ token, accountIdentifiers = [], readOnly = false, prefillLoading = false,
  allowListNameEdit = false }) => {
  const {
    listRfqData,
    rows,
    listRfqErrors,
    rowErrors,
    setListRfqData,
    addRow,
    removeRow,
    updateRow,
    // applyToAllRows,  // parked with the "Set all" shortcuts below
    validateRowCusip,
    clearListRfqError,
  } = useListRfqStore();
  const handleValidateCusip = useCallback(
    (rowId) => validateRowCusip(token, rowId),
    [token, validateRowCusip]
  );

  const [dealerOptions, setDealerOptions] = useState([]);

  // List-level Dealers field (MarketAxess only) open/closed state - see mktxDealerAutocompleteSx
  // below for why this exists: the trick that keeps the field's height correct hides the
  // Autocomplete's own input text while closed, so the preview text can show in its place
  // without the two overlapping; while open, the native input needs to be visible again so the
  // trader can actually type a filter.
  const [mktxDealerDropdownOpen, setMktxDealerDropdownOpen] = useState(false);

  // Bond details for one row's identifier, opened from the row's info icon.
  //
  // State lives here rather than in ListRfqBondRow so the grid mounts one dialog instead of
  // one per row. Copied from Pages/Inventory.jsx handleCusipClick (~line 288) - same API,
  // same three pieces of state, same modal - so a bond looks identical however it is opened.
  const [bondDetailsOpen, setBondDetailsOpen] = useState(false);
  const [bondDetailsLoading, setBondDetailsLoading] = useState(false);
  const [selectedBondDetails, setSelectedBondDetails] = useState(null);

  // The book for the same bond, shown under the reference data in the same dialog. Held
  // separately from the details above because the two calls are independent: whichever answers
  // first paints, so a slow quote fetch never holds up the bond's own fields.
  const [marketData, setMarketData] = useState(null);
  const [marketDataDepth, setMarketDataDepth] = useState([]);
  const [marketDataError, setMarketDataError] = useState(null);
  const [marketDataLoading, setMarketDataLoading] = useState(false);

  // >957 Strategy Params (MarketAxess only) - one shared dialog for the whole grid, the same
  // "one dialog, not one per row" shape as Bond Details above. strategyParamsRowId (not a
  // boolean) is both the open flag and which row's values the dialog edits - null means
  // closed. Only one row's dialog can ever be open at once, so strategyParamsDefs holds just
  // that row's definitions, not a map of every type seen so far - optionsHelper's
  // loadStrategyParamDefs/getCachedStrategyParamDefs is the actual per-type cache, shared with
  // listRfqPayload.buildListRfqPayload at submit time (see that module's own comment for why
  // it has to live outside React state).
  //
  // Edits are staged in the dialog's own draft state and only reach the row on OK
  // (StrategyParamsModal's own doc comment has the full reasoning) - strategyParamsInitialValues
  // is what seeds that draft each time the dialog opens for a row.
  const [strategyParamsRowId, setStrategyParamsRowId] = useState(null);
  const [strategyParamsLoading, setStrategyParamsLoading] = useState(false);
  const [strategyParamsError, setStrategyParamsError] = useState(null);
  const [strategyParamsDefs, setStrategyParamsDefs] = useState([]);
  const [strategyParamsInitialValues, setStrategyParamsInitialValues] = useState({});
  const strategyParamsRequestRef = useRef(0);

  // rowId -> the strategyType its strategyParameters were last auto-defaulted for. A plain
  // ref, not state: it only gates which rows the auto-fill effect below still needs to visit,
  // never drives a render itself. See that effect for why defaults are applied automatically
  // rather than only inside the dialog.
  const strategyParamsAutoFilledFor = useRef(new Map());

  // Opening one bond's details then quickly another's leaves two calls in flight, and they can
  // land out of order. Only the newest request is allowed to write state, so the dialog can
  // never show bond A's book under bond B's details. Same guard, and the same reason, as
  // marketDataRequestRef in ActualOrderTab.
  const marketDataRequestRef = useRef(0);

  const handleShowBondDetails = useCallback(
    async (cusip) => {
      if (!token || !cusip) return;
      setSelectedBondDetails(null);
      setBondDetailsLoading(true);
      setBondDetailsOpen(true);

      // Fired before the details await below rather than after it, so the two run side by side.
      // Not awaited: the dialog is already open and MarketDataInfoPanel shows its own spinner.
      const requestId = ++marketDataRequestRef.current;
      setMarketData(null);
      setMarketDataDepth([]);
      setMarketDataError(null);
      setMarketDataLoading(true);
      fetchMarketDataDetail(token, cusip)
        .then((response) => {
          if (requestId !== marketDataRequestRef.current) return;
          const normalized = normalizeMarketDataDetail(response);
          if (normalized) {
            setMarketData(normalized.top);
            setMarketDataDepth(normalized.depth);
            setMarketDataError(null);
          } else {
            // A bond with no live book is an ordinary answer, not a failure - say so plainly
            // rather than showing an empty table. Same wording as the single-bond ticket.
            setMarketData(null);
            setMarketDataDepth([]);
            setMarketDataError(
              response?.message || "Market data is not available for this security."
            );
          }
        })
        .catch(() => {
          if (requestId !== marketDataRequestRef.current) return;
          setMarketData(null);
          setMarketDataDepth([]);
          setMarketDataError(
            "Something went wrong while fetching market data. Please try again."
          );
        })
        .finally(() => {
          if (requestId === marketDataRequestRef.current) setMarketDataLoading(false);
        });

      try {
        // Returns {error: true, message} on a 200 + success:false, which BondDetailsModal
        // renders as its "no details" message - so no separate not-found branch is needed.
        const details = await fetchSecurityInfo(token, cusip);
        setSelectedBondDetails(details);
      } catch (e) {
        setSelectedBondDetails({
          error: true,
          message: e.message || "Failed to fetch bond details.",
        });
      } finally {
        setBondDetailsLoading(false);
      }
    },
    [token]
  );

  /**
   * Fetches (cached) definitions for one strategy type and fills any MISSING value on one row
   * from each definition's own defaultValue - merge-only, exactly `mergeStrategyParamDefaults`
   * on the single-bond ticket, never overwriting a value already there.
   *
   * Re-reads the row from the store rather than trusting a closure over `rows`: this is async
   * (a real fetch on a cache miss), and the trader may have opened the dialog and started
   * editing, or the row's strategyType may have moved on again, before it resolves - bails out
   * of both rather than writing something stale.
   */
  const applyDefaultStrategyParams = useCallback(
    async (rowId, strategyType) => {
      try {
        const defs = await loadStrategyParamDefs(token, strategyType);
        if (!defs.length) return;

        const currentRow = useListRfqStore.getState().rows.find((r) => r.rowId === rowId);
        if (!currentRow || currentRow.strategyType !== strategyType) return;

        const withDefaults = { ...(currentRow.strategyParameters || {}) };
        let changed = false;
        defs.forEach((def) => {
          const key = def.paramName;
          if (!key) return;
          // An explicit empty value is a trader choice to omit this optional parameter.
          // Only seed a parameter that has never been set on this row.
          if (Object.prototype.hasOwnProperty.call(withDefaults, key)) return;
          const normalized = normalizeYesNo(def.defaultValue);
          if (normalized === undefined || normalized === null || normalized === "") return;
          withDefaults[key] = normalized;
          changed = true;
        });
        if (changed) updateRow(rowId, { strategyParameters: withDefaults });
      } catch (err) {
        // A background convenience default, not a user-initiated action - the Strategy Params
        // dialog still lets the trader fill these in by hand if this silently fails.
        console.error("Error pre-filling strategy param defaults:", err);
      }
    },
    [token, updateRow]
  );

  /**
   * Applies strategy-parameter defaults to every row automatically, the same way the
   * single-bond ticket does (ActualOrderTab.loadStrategyParams runs the instant a strategy
   * type is set, with no confirmation needed) - NOT only when the trader happens to open the
   * Strategy Params dialog for a row.
   *
   * This exists because of a real, confirmed gap: the dialog-only version of this feature
   * left every list row with zero >957 parameters unless a trader manually opened and OK'd
   * each one, while the single ticket always sends five (AdaptiveAutoXUrgency, MinNumResp,
   * AutoStart, ADXHedging, RFQDelayStartMinutes) - AutoStart=Y in particular looks like what
   * Adaptive Auto-X actually needs to begin working an order at all. Traced on real dev
   * 2026-09-28: every order carrying all five traded; a list built through the dialog-only
   * version, carrying none, sat accepted and unfilled for over 45 minutes across five orders.
   *
   * strategyParamsAutoFilledFor tracks rowId -> the strategyType last defaulted for, so this
   * only fires once per row per type (a new row, or an existing row's type changing -
   * ListRfqBondRow's strategyType onChange already clears strategyParameters back to {} on a
   * change, matching the single ticket's own "isTypeChanged -> reset" rule) - never on a
   * render that changed nothing, and never overwriting a trader's own edit (the row already
   * has values by then, so the Map entry already matches and this is skipped).
   */
  useEffect(() => {
    if (!token || readOnly || listRfqData.protocol !== PROTOCOL_TYPES.MARKETAXESS) return;
    rows.forEach((row) => {
      if (!row.strategyType) return;
      if (strategyParamsAutoFilledFor.current.get(row.rowId) === row.strategyType) return;
      strategyParamsAutoFilledFor.current.set(row.rowId, row.strategyType);
      applyDefaultStrategyParams(row.rowId, row.strategyType);
    });
  }, [rows, token, readOnly, listRfqData.protocol, applyDefaultStrategyParams]);

  const handleOpenStrategyParams = useCallback(
    async (rowId) => {
      const requestId = ++strategyParamsRequestRef.current;
      setStrategyParamsRowId(rowId);
      setStrategyParamsError(null);
      setStrategyParamsDefs([]);

      const row = rows.find((r) => r.rowId === rowId);
      const strategyType = row?.strategyType || MKTX_STRATEGY_TYPES.TAKE;

      setStrategyParamsLoading(true);
      try {
        // Cached by strategy type in optionsHelper (see its own comment) - a real network
        // call only the first time any row opens this dialog for a given type. Re-read the
        // row after the request: the background default fill may have completed meanwhile.
        const defs = await loadStrategyParamDefs(token, strategyType);
        if (requestId !== strategyParamsRequestRef.current) return;
        const currentRow = useListRfqStore.getState().rows.find((item) => item.rowId === rowId);
        if (!currentRow || currentRow.strategyType !== strategyType) {
          setStrategyParamsError("This bond changed while its parameters were loading. Reopen Strategy Params.");
          return;
        }
        // The background default load may have updated the row while this request was in
        // flight. Start from the latest row, and fill any still-unset defaults locally so
        // opening the dialog immediately never presents an empty draft for a defaulted type.
        const values = { ...(currentRow.strategyParameters || {}) };
        if (!readOnly) defs.forEach((def) => {
          if (!def.paramName || Object.prototype.hasOwnProperty.call(values, def.paramName)) return;
          const defaultValue = normalizeYesNo(def.defaultValue);
          if (defaultValue !== undefined && defaultValue !== null && defaultValue !== "") {
            values[def.paramName] = defaultValue;
          }
        });
        setStrategyParamsDefs(defs);
        setStrategyParamsInitialValues(values);
      } catch (err) {
        if (requestId === strategyParamsRequestRef.current) {
          setStrategyParamsError(err?.message || "Unable to load strategy parameters");
        }
      } finally {
        if (requestId === strategyParamsRequestRef.current) setStrategyParamsLoading(false);
      }
    },
    [rows, token, readOnly]
  );

  const handleSaveStrategyParams = useCallback(
    (values) => {
      strategyParamsRequestRef.current += 1;
      if (strategyParamsRowId) updateRow(strategyParamsRowId, { strategyParameters: values });
      setStrategyParamsRowId(null);
    },
    [strategyParamsRowId, updateRow]
  );

  const handleCancelStrategyParams = useCallback(() => {
    strategyParamsRequestRef.current += 1;
    setStrategyParamsRowId(null);
  }, []);

  // Dealers come from the same source the single ticket uses, keyed on protocol. The helper
  // caches per protocol, so opening this tab after the Bond Order tab costs nothing.
  //
  // Keyed on listRfqData.protocol (2026-09-25), not hardcoded to Trumid - MarketAxess is now
  // a real option in the Protocol select below, and each venue has its own dealer list
  // (PROTOCOL_TO_DEALER_PARAM maps both). Re-fetches whenever the trader switches protocol,
  // the same way ActualOrderTab's own dealer effect keys on the single ticket's protocol.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const venues = await updateVenues(token, listRfqData.protocol || PROTOCOL_TYPES.TRUMID_RFQ);
      if (!cancelled) setDealerOptions(filterVenueOptions({
        venues: Array.isArray(venues) ? venues : [],
        protocol: listRfqData.protocol,
      }));
    };
    if (token) load();
    return () => {
      cancelled = true;
    };
  }, [token, listRfqData.protocol]);

  // Settlement is calculated, not chosen. Fetched once when the tab opens and shown
  // read-only on every row - the same thing ActualOrderTab does for a single order (its
  // loadSettlementDate effect), and useOrderEntry re-fetches it at submit so the value
  // actually sent is always current.
  useEffect(() => {
    if (!token || listRfqData.settlementDate) return undefined;
    let cancelled = false;
    const loadSettlementDate = async () => {
      const settlementDate = await fetchBondCalcSettlementDate(token);
      if (!cancelled && settlementDate) setListRfqData({ settlementDate });
    };
    loadSettlementDate();
    return () => {
      cancelled = true;
    };
  }, [token, listRfqData.settlementDate, setListRfqData]);

  // Default the account to the first available, as actualOrderStore does for one order.
  useEffect(() => {
    if (!listRfqData.account && accountIdentifiers.length > 0) {
      setListRfqData({ account: accountIdentifiers[0] });
    }
  }, [accountIdentifiers, listRfqData.account, setListRfqData]);

  const dealerChoices = useMemo(() => {
    if (listRfqData.protocol !== PROTOCOL_TYPES.MARKETAXESS) {
      return [ALL_DEALER_OPTION, ...dealerOptions.filter((o) => o !== ALL_DEALER_OPTION)];
    }
    const selected = Array.isArray(listRfqData.mktxVenue) ? listRfqData.mktxVenue : [];
    return [ALL_DEALER_OPTION, ...dealerOptions.filter((code) => code !== ALL_DEALER_OPTION),
      ...selected.filter((code) => code !== ALL_DEALER_OPTION && !dealerOptions.includes(code))];
  }, [dealerOptions, listRfqData.protocol, listRfqData.mktxVenue]);

  const pricingProcessOptions = useMemo(() => {
    const base = fieldOptions.mktxPricingProcess || [];
    const selected = listRfqData.mktxPricingProcess;
    return selected && !base.includes(selected) ? [...base, selected] : base;
  }, [listRfqData.mktxPricingProcess]);

  // MarketAxess's list-level dealer field. Mirrors ListRfqBondRow's own selectedVenues/
  // formatVenues pair exactly (down to the 25-character cut-off) so the same selection looks
  // the same however it is shown - copied rather than imported because both are module-private
  // to their own file, the same reason formatSettlementDate above is copied rather than shared.
  // Not memoised, for the same reason formatVenues in ListRfqBondRow is not: joining a handful
  // of strings is cheap enough that memoising it would only add a dependency array to keep in
  // step, matching that file's own choice not to bother for the same computation.
  const mktxSelectedVenues = Array.isArray(listRfqData.mktxVenue) ? listRfqData.mktxVenue : [];
  const mktxDealerPreview = (() => {
    if (mktxSelectedVenues.length === 0) return "";
    const joined = mktxSelectedVenues.join(", ");
    return joined.length > 25 ? joined.substring(0, 22) + "..." : joined;
  })();

  /**
   * Copied - not paraphrased - from the single-bond ticket's own Dealer field
   * (ActualOrderTabOrderPrimaryFields.jsx, its `dealerAutocompleteSx`), field for field, the
   * same reason formatSettlementDate above is copied rather than shared. It exists because a
   * multi-select Autocomplete's default filled-variant padding was written for wrapped chips,
   * not a single preview line, and overriding only `.MuiAutocomplete-inputRoot` (this file's
   * own first attempt at fixing the resulting height) loses the specificity fight against
   * MUI's own combined `.MuiFilledInput-root.MuiAutocomplete-inputRoot` rule - the single
   * ticket's version targets that exact combined selector, which is the part the first
   * attempt here was missing, not the padding numbers themselves.
   */
  const mktxDealerAutocompleteSx = {
    ...inputStyle,
    position: "relative",
    "& .MuiFilledInput-root.MuiAutocomplete-inputRoot": {
      ...inputStyle["& .MuiFilledInput-root"],
      paddingTop: "10px !important",
      paddingRight: "55px !important",
      paddingBottom: "0 !important",
      paddingLeft: "0 !important",
      minHeight: "auto",
      height: "auto",
      "&.Mui-disabled": {
        ...(inputStyle["& .MuiFilledInput-root"]["&.Mui-disabled"] || {}),
        backgroundColor: tokens.colors.white,
      },
    },
    "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
      padding: "14px 12px !important",
      minWidth: 0,
      textOverflow: "ellipsis",
      // Hides the native input's own text/caret while the preview Typography below is
      // showing instead, so the two never render on top of each other - visible again the
      // moment the field opens, so the trader can see what they are typing to filter.
      color: mktxDealerDropdownOpen ? tokens.colors.fontColour : "transparent",
      caretColor: mktxDealerDropdownOpen ? tokens.colors.fontColour : "transparent",
      cursor: readOnly ? "not-allowed" : "text",
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "50%",
      transform: "translateY(-50%)",
      right: "8px",
      marginLeft: 0,
      display: "flex",
      gap: 0,
    },
    "& .MuiAutocomplete-clearIndicator": {
      padding: "4px",
      marginRight: 0,
    },
    "& .MuiAutocomplete-popupIndicator": {
      padding: "4px",
    },
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "nowrap",
    },
  };

  const handleListField = useCallback(
    (name, value) => {
      clearListRfqError(name);
      // A name still on the old protocol's default follows the protocol; a typed one is kept.
      if (name === "protocol" && listRfqData.listName === defaultListName(listRfqData.protocol)) {
        setListRfqData({ protocol: value, listName: defaultListName(value) });
        return;
      }
      setListRfqData({ [name]: value });
    },
    [clearListRfqError, setListRfqData, listRfqData.listName, listRfqData.protocol]
  );

  // ALL is mutually exclusive with naming individual dealers - same rule, and the same shape of
  // handler, as ListRfqBondRow.handleDealers (that one patches one row; this one patches the
  // single list-level field every bond will be sent with).
  const handleMktxDealers = useCallback(
    (_event, values, reason, details) => {
      const selection = Array.from(
        new Set((values || []).map((v) => String(v).trim()).filter(Boolean))
      );
      const hasAll = selection.includes(ALL_DEALER_OPTION);
      const dealers = selection.filter((v) => v !== ALL_DEALER_OPTION);

      let next;
      if (reason === "clear") next = [];
      else if (details?.option === ALL_DEALER_OPTION) next = hasAll ? [ALL_DEALER_OPTION] : [];
      else next = dealers;

      handleListField("mktxVenue", next);
    },
    [handleListField]
  );

  // Recomputed as the trader types, not at submit: the clash is shown the moment it is
  // created. Cheap enough to do on every keystroke - one pass over a handful of rows - and
  // memoised so unrelated state changes do not redo it.
  const duplicateRowIds = useMemo(() => findDuplicateCusips(rows), [rows]);

  const atMaxBonds = rows.length >= MAX_LIST_RFQ_BONDS;

  // MarketAxess joined the Protocol select 2026-09-25 (previously the only option). The
  // Quote Deadline and Quote Firm Time are Trumid-only. MarketAxess shows Inquiry Type as
  // disabled BIN, matching the single ticket; the list payload also fixes it to BIN.
  // MarketAxess gets its own list-level Pricing Process, Dealer List Mode and Dealers fields,
  // immediately below. Strategy, Item Dealers and Effective/Expiry Time are MarketAxess's
  // per-bond fields instead, rendered per row by ListRfqBondRow, not here.
  const isTrumid = listRfqData.protocol === PROTOCOL_TYPES.TRUMID_RFQ;
  const isBin = isTrumid && listRfqData.rfqInquiryType === TRUMID_RFQ_INQUIRY_TYPES.BIN;
  // The single MarketAxess ticket hides both dealer controls for SEEK/PROVIDE.
  // With a list they apply if any populated bond uses TAKE.
  const enteredRows = meaningfulRows(rows);
  const showMktxDealerTargeting = !isTrumid &&
    (enteredRows.length ? enteredRows : rows).some((row) =>
      mktxStrategyUsesDealers(row.strategyType));
  const visibleDealerScope = showMktxDealerTargeting ? 'BOTH' : 'LIST';

  // Fill the remaining space after the visible list fields, as in Trumid.
  const mktxFieldCount = 5 // Account, Protocol, Inquiry Type, Pricing Process and List Name
    + (showMktxDealerTargeting ? 2 : 0)
    + (listRfqData.settlementDate ? 1 : 0);
  const commentsXs = isTrumid
    ? 12 - 3 - (listRfqData.settlementDate ? 3 : 0) - (isBin ? 3 : 0)
    : 12 - (mktxFieldCount % 4) * 3;
  const filledCount = meaningfulRows(rows).length;

  // Which row the Strategy Params dialog is showing - null safely when no row is open, since
  // StrategyParamsModal's `open` prop is what actually gates rendering it.
  const strategyParamsRow = rows.find((r) => r.rowId === strategyParamsRowId) || null;

  const sectionTitle = (text) => (
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 700,
        color: tokens.colors.fontColour,
        pb: 0.5,
        mb: 1.25,
        borderBottom: `2px solid ${tokens.colors.bodyBorderColor}`,
      }}
    >
      {text}
    </Typography>
  );

  return (
    // A column that fills the tab and never scrolls as a whole: the enquiry fields, the
    // section headings and the Add Identifier button stay put, and only the bond rows scroll
    // when there are more of them than fit. minHeight 0 is what lets the rows box shrink
    // inside the flex column instead of pushing the tab taller.
    <Box
      sx={{
        p: 2,
        pb: 0.5,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* No heading over these fields: the Bond Order tab opens straight into its inputs and
          this tab now matches it. The "Bonds" heading below stays - it separates two groups,
          where this one only labelled the top of the form. */}
      <Grid container spacing={1.5}>
        {/* The trader's name for the list - sent to MA as 5634 on creation; never sent to Trumid. Optional unless
            LIST_RFQ_NAME_REQUIRED is on; capped at the backend column's 40 characters, with a
            counter like the bond ladder name. The first field of the form. */}
        <Grid item xs={3}>
          <TextField
            fullWidth
            label="List Name"
            required={LIST_RFQ_NAME_REQUIRED}
            value={listRfqData.listName || ""}
            onChange={(e) => {
              if (e.target.value.length <= MAX_LIST_RFQ_NAME_LENGTH) {
                handleListField("listName", e.target.value);
              }
            }}
            variant="filled"
            error={!!listRfqErrors.listName}
            disabled={readOnly && !allowListNameEdit}
            sx={inputStyle}
            InputProps={{
              disableUnderline: true,
              endAdornment: (
                <Typography
                  variant="caption"
                  sx={{ color: tokens.colors.fontColour80, whiteSpace: "nowrap", pointerEvents: "none" }}
                >
                  {(listRfqData.listName || "").length}/{MAX_LIST_RFQ_NAME_LENGTH}
                </Typography>
              ),
            }}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            fullWidth
            select
            label="Account"
            value={listRfqData.account || ""}
            onChange={(e) => handleListField("account", e.target.value)}
            variant="filled"
            error={!!listRfqErrors.account}
            disabled={readOnly}
            sx={inputStyle}
            InputProps={{ disableUnderline: true }}
          >
            {accountIdentifiers.map((identifier) => (
              <MenuItem key={identifier} value={identifier}>
                {identifier}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Protocol for the whole list. A real select rather than a fixed caption because
            more than one venue can have a list form - MarketAxess joined 2026-09-25
            (OrdersCommandHandler.createOrderList's protocol guard now accepts
            "MarketAxess RFQ" alongside "Trumid RFQ"). Adding a venue is then a MenuItem plus
            whatever fields that venue needs, with no change to the shape of this screen.

            The single-bond ticket puts Protocol on the same footing (a select in
            ActualOrderTabOrderPrimaryFields), so the two tabs read alike. */}
        <Grid item xs={3}>
          <TextField
            fullWidth
            select
            label="Protocol"
            value={listRfqData.protocol || ""}
            onChange={(e) => handleListField("protocol", e.target.value)}
            variant="filled"
            error={!!listRfqErrors.protocol}
            disabled={readOnly}
            sx={inputStyle}
            InputProps={{ disableUnderline: true }}
          >
            <MenuItem value={PROTOCOL_TYPES.TRUMID_RFQ}>{PROTOCOL_TYPES.TRUMID_RFQ}</MenuItem>
            <MenuItem value={PROTOCOL_TYPES.MARKETAXESS}>{PROTOCOL_TYPES.MARKETAXESS}</MenuItem>
          </TextField>
        </Grid>

        {/* Trumid's inquiry choice is editable. The MarketAxess ticket below shows its
            fixed BIN value, as the single-order ticket does. */}
        {isTrumid && (
          <Grid item xs={3}>
            <TextField
              fullWidth
              select
              label="RFQ Inquiry Type"
              value={listRfqData.rfqInquiryType || ""}
              onChange={(e) => handleListField("rfqInquiryType", e.target.value)}
              variant="filled"
              error={!!listRfqErrors.rfqInquiryType}
              disabled={readOnly}
              sx={inputStyle}
              InputProps={{ disableUnderline: true }}
            >
              <MenuItem value={TRUMID_RFQ_INQUIRY_TYPES.ASAP}>ASAP</MenuItem>
              <MenuItem value={TRUMID_RFQ_INQUIRY_TYPES.BIN}>BIN</MenuItem>
            </TextField>
          </Grid>
        )}

        {!isTrumid && (
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="Inquiry Type"
              value={MKTX_INQUIRY_TYPES.BIN}
              variant="filled"
              disabled
              sx={inputStyle}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
        )}

        {/* 5626 MktxPricingProcess - MarketAxess only. List-level, not per row: the backend
            requires every bond to carry the same value (requireSameListLevelValue) and has no
            null guard on it at all (FixNewOrderMapper.mktxPricingProcess), so this is a real
            select with a value always present (see emptySharedFields), not a field the trader
            can leave blank. */}
        {!isTrumid && (
          <Grid item xs={3}>
            <TextField
              fullWidth
              select
              label="Pricing Process"
              value={listRfqData.mktxPricingProcess || MKTX_PRICING_PROCESSES.ONESTEP}
              onChange={(e) => handleListField("mktxPricingProcess", e.target.value)}
              variant="filled"
              error={!!listRfqErrors.mktxPricingProcess}
              disabled={readOnly}
              sx={inputStyle}
              InputProps={{ disableUnderline: true }}
            >
              {pricingProcessOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {MKTX_PRICING_PROCESS_DISPLAY[option] || option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {/* 20002 IncludeDealers, the master switch - see listRfqStore.emptySharedFields's
            mktxDealerListMode comment. Without a mode chosen, the Dealers field below has no
            effect on the wire at all, so this sits right before it. */}
        {showMktxDealerTargeting && (
          <Grid item xs={3}>
            <TextField
              fullWidth
              select
              label="Dealer List Mode"
              value={listRfqData.mktxDealerListMode || ""}
              onChange={(e) => handleListField("mktxDealerListMode", e.target.value)}
              variant="filled"
              error={!!listRfqErrors.mktxDealerListMode}
              disabled={readOnly}
              sx={inputStyle}
              InputProps={{ disableUnderline: true }}
            >
              {fieldOptions.mktxDealerListMode.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {/* 9690 list-level dealers. List targeting shows this picker and the
            per-bond 20130 picker; Dealer List Mode remains list-level in both cases. */}
        {showMktxDealerTargeting && (
          <Grid item xs={3}>
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              openOnFocus
              options={dealerChoices}
              value={
                mktxSelectedVenues.includes(ALL_DEALER_OPTION)
                  ? [ALL_DEALER_OPTION]
                  : mktxSelectedVenues
              }
              onChange={handleMktxDealers}
              onOpen={() => setMktxDealerDropdownOpen(true)}
              onClose={() => setMktxDealerDropdownOpen(false)}
              disabled={readOnly}
              isOptionEqualToValue={(option, value) => option === value}
              getOptionLabel={(option) => option || ""}
              renderOption={(props, option, { selected }) => (
                <Box component="li" {...props} sx={{ display: "flex", alignItems: "center" }}>
                  {option !== ALL_DEALER_OPTION && (
                    <Checkbox
                      checked={selected}
                      sx={{ width: 16, height: 16, p: 0, mr: 1, borderRadius: 0 }}
                      disableRipple
                    />
                  )}
                  <Typography sx={{ fontSize: 13, fontWeight: selected ? 700 : 400 }}>
                    {option}
                  </Typography>
                </Box>
              )}
              renderTags={() => null}
              renderInput={(params) => (
                <Box sx={{ position: "relative" }}>
                  <TextField
                    {...params}
                    fullWidth
                    variant="filled"
                    label="Dealers"
                    error={!!listRfqErrors.mktxVenue}
                    sx={mktxDealerAutocompleteSx}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      shrink: true,
                    }}
                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                  />
                  {!mktxDealerDropdownOpen && !!mktxDealerPreview && (
                    <Typography
                      sx={{
                        position: "absolute",
                        left: "12px",
                        right: "55px",
                        top: "50%",
                        transform: "translateY(calc(-50% + 10px))",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        pointerEvents: "none",
                        color: readOnly ? tokens.colors.disabledFontColour : tokens.colors.fontColour,
                        fontSize: "0.95rem",
                        lineHeight: "1.25rem",
                      }}
                    >
                      {mktxDealerPreview}
                    </Typography>
                  )}
                </Box>
              )}
            />
          </Grid>
        )}

        {isTrumid && (
          <Grid item xs={3}>
            <NumericFormat
              customInput={TextField}
              fullWidth
              label="Quote Deadline (min)"
              placeholder="2-30"
              value={listRfqData.quoteSubmissionDeadline ?? ""}
              variant="filled"
              allowNegative={false}
              decimalScale={0}
              disabled={readOnly}
              error={!!listRfqErrors.quoteSubmissionDeadline}
              sx={inputStyle}
              InputProps={{ disableUnderline: true }}
              isAllowed={(values) => {
                const { floatValue, value } = values;
                if (floatValue === undefined || value === "") return true;
                return Number.isInteger(floatValue) && floatValue <= 30;
              }}
              onValueChange={(v) =>
                handleListField("quoteSubmissionDeadline", v.value === "" ? "" : v.floatValue)
              }
            />
          </Grid>
        )}

        {/* Settlement is calculated, not chosen, so it is shown once for the enquiry and
            read-only - the same field the Bond Order tab shows
            (ActualOrderTabOrderProtocolSection: filled, labelled, disabled, rendered only
            once a date exists). It is still sent PER BOND: buildListRfqPayload writes tag
            64 onto every line item, because the spec puts 64 inside the <146> group. */}
        {listRfqData.settlementDate && (
          <Grid item xs={3}>
            <TextField
              fullWidth
              variant="filled"
              label="Settlement Date"
              value={formatSettlementDate(listRfqData.settlementDate)}
              disabled
              sx={inputStyle}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
        )}

        {/* 28019 is BIN-only (spec p.46), so it appears only when BIN is chosen - the same
            condition TruMidRFQFields uses for the single-bond ticket. */}
        {isBin && (
          <Grid item xs={3}>
            <NumericFormat
              customInput={TextField}
              fullWidth
              label="Quote Firm Time (min)"
              placeholder="2-30"
              value={listRfqData.ttl ?? ""}
              variant="filled"
              allowNegative={false}
              decimalScale={0}
              disabled={readOnly}
              error={!!listRfqErrors.ttl}
              sx={inputStyle}
              InputProps={{ disableUnderline: true }}
              isAllowed={(values) => {
                const { floatValue, value } = values;
                if (floatValue === undefined || value === "") return true;
                return Number.isInteger(floatValue) && floatValue <= 30;
              }}
              onValueChange={(v) => handleListField("ttl", v.value === "" ? "" : v.floatValue)}
            />
          </Grid>
        )}
        {/* Comments for the whole list, sharing the last row of the fields above rather than
            taking a row of its own - it describes the enquiry, the same reason Account and
            Protocol are up here, so it belongs in the same block.

            Width is whatever the last row has left. commentsXs accounts for the visible
            venue-specific fields and Settlement Date; a full row leaves Comments its own row.

            Uses the very same component the Bond Order tab uses (ActualOrderTabOrderComments),
            so the box, the 200-character counter and the focus ring are identical rather than
            re-implemented.

            One comment typed, then copied onto every bond at submit: the backend keeps no
            list-level record, so buildListRfqPayload puts the same text on each line item and
            OrdersCommandHandler.buildOrder stores it per order, exactly as a single-bond
            order does. It never reaches the venue - no Trumid service or mapper reads
            comments, so it stays on order_mst and is not a FIX tag. */}
        <OrderComments
          xs={commentsXs}
          comments={listRfqData.comments}
          handleChange={(e) => handleListField("comments", e.target.value)}
          isFieldEditable={() => !readOnly}
        />
      </Grid>


      <Box sx={{ mt: 1, flexShrink: 0 }}>{sectionTitle(
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
          {`Bonds (${filledCount})`}
          {prefillLoading && <CircularProgress size={16} aria-label="Loading list bonds" />}
        </Box>
      )}</Box>

      {/* ---------------------------------------------------------------------------
          "Set all" shortcuts - PRICE / SPREAD / Copy row 1 - parked, not deleted.

          They pushed one per-line-item value onto every row at once. Useful on a long
          uniform list, but not needed yet, so they are commented out rather than removed
          to keep the working version to hand. Nothing else depends on them: every field
          they set is still editable on each row, and applyToAllRows stays in the store.

          To restore: uncomment this block and the applyToAllRows destructure above.

          NOTE (was a JSX comment here): setting a column once and pushing it down was a
          convenience only - each row kept its own value afterwards, and the request still
          carried a per-line-item value for every bond.
          <Box
          sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mb: 1,
          flexWrap: "wrap",
          flexShrink: 0,
          }}
          >
          <Typography sx={{ fontSize: 11, color: tokens.colors.fontColour85 }}>
          Set all:
          </Typography>
          {[
          // Only the price type is set; applyToAllRows runs withStipulationRule, which
          // clears the stipulation on PRICE and defaults it to SPOT on SPREAD.
          { label: "PRICE", patch: { priceType: TRUMID_PRICE_TYPES.PRICE } },
          { label: "SPREAD", patch: { priceType: TRUMID_PRICE_TYPES.SPREAD } },
          ].map((preset) => (
          <Button
          key={preset.label}
          size="small"
          variant="text"
          disabled={readOnly}
          onClick={() => applyToAllRows(preset.patch)}
          sx={{ textTransform: "none", fontSize: 11, py: 0, minWidth: 0, px: 0.75 }}
          >
          {preset.label}
          </Button>

          ))}
          <Tooltip title="Copy the first bond's dealers and partial-fill setting onto every bond">
          <span>
          <Button
          size="small"
          variant="text"
          disabled={readOnly || rows.length === 0}
          onClick={() =>
          applyToAllRows({
          venue: [...(rows[0]?.venue || [])],
          allowPartialExecution: rows[0]?.allowPartialExecution,
          })
          }
          sx={{ textTransform: "none", fontSize: 11, py: 0, minWidth: 0, px: 0.75 }}
          >
          Copy row 1
          </Button>
          </span>
          </Tooltip>
          </Box>
          --------------------------------------------------------------------------- */}

      {/* ONE scrolling element, not two.
          An earlier version scrolled the outer box sideways and an inner box vertically,
          which produced two horizontal scrollbars: CSS computes overflow-x to `auto` on any
          element whose overflow-y is not `visible`, so the inner box scrolled sideways as
          well. Here a single scroller handles both axes and the header is made sticky, so
          there is exactly one horizontal bar and one vertical bar.
          Add Identifier sits outside the scroller so it stays reachable and is never clipped. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          // Colours taken from the Market Data table on the Bond Order tab. That one is a
          // real MUI <Table>, so it gets its palette from the global grid rules rather than
          // from its own sx - theme.js MuiTableHead/MuiTableCell/MuiTableRow (~line 514) and
          // the matching !important block in index.css (~line 87). This grid is built from
          // Boxes, so none of that reaches it and the same three tokens are set by hand:
          // white rows, defaultGridHeaderBackground for the header band, and
          // defaultGridBorderColor for every rule. Only the colours are borrowed - the
          // spacing and the 6px radius are this grid's own.
          bgcolor: tokens.colors.white,
          border: `1px solid ${tokens.colors.defaultGridBorderColor}`,
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Box sx={{ minWidth: listRfqRowMinWidth(readOnly, listRfqData.protocol, visibleDealerScope) }}>
            <Box
              sx={{
                display: "flex",
                px: 0.75,
                // py 0.5, the same vertical padding a Market Data header cell uses. The band
                // is still taller than it was, because the 14px heading is taller than the
                // 11px one it replaces.
                py: 0.5,
                position: "sticky",
                top: 0,
                zIndex: 2,
                // The grey band. MuiTableHead paints this on a real table; here it is set
                // directly, from the same token.
                backgroundColor: tokens.colors.defaultGridHeaderBackground,
                borderBottom: `1px solid ${tokens.colors.defaultGridBorderColor}`,
              }}
            >
              {listRfqColumns(readOnly, listRfqData.protocol, visibleDealerScope).map((column) => (
                <Box key={column.key} sx={listRfqColumnSx(column)}>
                  {/* Plain text, no Tooltip: the headings carried the column's FIX tag on
                      hover, which is spec detail a trader does not need mid-ticket. The tag
                      is still recorded against each column in LIST_RFQ_COLUMNS, where it is
                      documentation for whoever reads the code rather than something the
                      screen shows. */}
                  {column.label && (
                    <Typography sx={gridHeaderTextStyle}>{column.label}</Typography>
                  )}
                </Box>
              ))}
            </Box>

            {rows.map((row) => (
              <ListRfqBondRow
                key={row.rowId}
                row={row}
                errors={rowErrors[row.rowId]}
                dealerChoices={dealerChoices}
                readOnly={readOnly}
                onChange={updateRow}
                onValidateCusip={handleValidateCusip}
                onRemove={removeRow}
                onShowDetails={handleShowBondDetails}
                onOpenStrategyParams={handleOpenStrategyParams}
                isDuplicate={duplicateRowIds.has(row.rowId)}
                protocol={listRfqData.protocol}
                dealerScope={visibleDealerScope}
              />
            ))}
          </Box>
        </Box>

        {!readOnly && <Box
          sx={{
            p: 0.75,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderTop: `1px solid ${tokens.colors.bodyBorderColor}`,
            backgroundColor: tokens.colors.defaultGridRowColor,
          }}
        >
          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            onClick={addRow}
            disabled={atMaxBonds}
            sx={addBondButtonStyle}
          >
            Add Identifier
          </Button>

          {atMaxBonds && (
            <Typography sx={{ fontSize: 11, color: tokens.colors.fontColour85 }}>
              {rows.length > MAX_LIST_RFQ_BONDS
                ? `This list has ${rows.length} bonds - the maximum is ${MAX_LIST_RFQ_BONDS}. Remove ${rows.length - MAX_LIST_RFQ_BONDS} to send it.`
                : `Maximum ${MAX_LIST_RFQ_BONDS} bonds per list.`}
            </Typography>
          )}

        </Box>}
      </Box>


      {/* Sits inside the order entry dialog. MUI portals each Dialog to document.body and
          stacks the most recently opened on top, so this opens over the ticket rather than
          behind it, and closing it leaves the ticket exactly as it was. */}
      <BondDetailsModal
        open={bondDetailsOpen}
        onClose={() => setBondDetailsOpen(false)}
        bondDetails={selectedBondDetails}
        loading={bondDetailsLoading}
        // Only this screen asks for the book. The dialog defaults it off, so the six other
        // places that open it are unchanged.
        showMarketData
        marketData={marketData}
        marketDataDepth={marketDataDepth}
        marketDataError={marketDataError}
        marketDataLoading={marketDataLoading}
      />

      <StrategyParamsModal
        open={!!strategyParamsRowId}
        onCancel={handleCancelStrategyParams}
        onSave={handleSaveStrategyParams}
        identifier={strategyParamsRow ? (strategyParamsRow.cusip || "").trim().toUpperCase() : ""}
        loading={strategyParamsLoading}
        error={strategyParamsError}
        params={strategyParamsDefs}
        initialValues={strategyParamsInitialValues}
        readOnly={readOnly}
      />
    </Box>
  );
};

export default ListRfqTab;
